// features/workstations/dashboard-sections/force-tracker.js
// ═══════════════════════════════════════════════════════════
// FORCE TRACKER — composed from primitives + custom force rows
// ═══════════════════════════════════════════════════════════
// 7-force model for QQQ/SPY (Pre-market, Macro, Earnings, Sector,
// Sentiment, VIX, Dollar). Reads the latest workstation_force_snapshots
// row and renders each force with its direction arrow, magnitude,
// notes, and expandable detail.
//
// Built from primitives:
//   Eyebrow / SerifHeadline / SerifSummary / SectionStrip /
//   PillTable / PillRow / PractitionerLens
// Plus one custom element: ForceRow (arrow + label + magnitude
// + notes + expandable detail).
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillTable,
  PillRow,
  PractitionerLens,
} from '../../../components/widgets/primitives';

// Direction → tone + arrow glyph
const DIRECTION = {
  bullish: { tone: 'sage',  arrow: '\u2191', label: 'Bullish' },
  bearish: { tone: 'terra', arrow: '\u2193', label: 'Bearish' },
  neutral: { tone: 'steel', arrow: '\u2192', label: 'Neutral' },
};

const MAGNITUDE_TONES = {
  high:   'gold',
  medium: 'amber',
  low:    'steel',
};

// Force order — high-impact at top, slow-moving below
const FORCE_ORDER = [
  { key: 'force_premarket', label: 'Pre-market', subtitle: 'QQQ/SPY gap + structure' },
  { key: 'force_macro',     label: 'Macro',      subtitle: 'Scheduled high-impact events' },
  { key: 'force_earnings',  label: 'Earnings',   subtitle: 'QQQ top 10 reporting' },
  { key: 'force_sector',    label: 'Sector',     subtitle: 'Tech vs broad market + breadth' },
  { key: 'force_sentiment', label: 'Sentiment',  subtitle: 'Put/Call ratio + breadth (contrarian)' },
  { key: 'force_vix',       label: 'VIX',        subtitle: 'Fear gauge + term structure' },
  { key: 'force_dollar',    label: 'Dollar',     subtitle: 'DXY trend (inverse to QQQ)' },
];

const POLL_MS = 60_000;

export default function ForceTrackerSection({ workstation }) {
  const domain = workstation?.domain_key || 'trading';
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotsToday, setSnapshotsToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchLatest() {
      try {
        const res = await fetch(`/api/intelligence/force-snapshot?domain=${encodeURIComponent(domain)}`, {
          cache: 'no-store',
        });
        if (cancelled) return;
        const data = res.ok ? await res.json() : null;
        if (!data) {
          setError('Could not load force snapshot');
          return;
        }
        setSnapshot(data.snapshot || null);
        setSnapshotsToday(data.snapshots_today ?? null);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Could not load force snapshot');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchLatest();
    const id = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        fetchLatest();
      }
    }, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [domain]);

  // ─── Loading / empty states ───
  if (loading && !snapshot) {
    return (
      <section className="ws-ftrack">
        <div className="ws-ftrack-loading">Loading…</div>
      </section>
    );
  }
  if (!snapshot) {
    return (
      <section className="ws-ftrack">
        <Eyebrow>qqq/spy forces · awaiting snapshot</Eyebrow>
        <SerifHeadline>No snapshot yet</SerifHeadline>
        <SerifSummary>
          The pre-market cron writes the first snapshot each morning at
          6:30 AM ET. To trigger one now: <code>/api/cron/intelligence-force-snapshot</code>.
        </SerifSummary>
        {error && <div className="ws-ftrack-error">{error}</div>}
      </section>
    );
  }

  const dir = DIRECTION[snapshot.net_bias] || DIRECTION.neutral;
  const bull = snapshot.bullish_count || 0;
  const bear = snapshot.bearish_count || 0;
  const neutral = 7 - bull - bear;
  const totalAligned = Math.max(bull, bear);
  const warnings = Array.isArray(snapshot.warning_flags) ? snapshot.warning_flags : [];

  return (
    <section className="ws-ftrack">
      <Eyebrow parts={[
        'qqq/spy forces',
        snapshotsToday ? `${snapshotsToday} snapshots today` : null,
        prettyWindow(snapshot.snapshot_window),
      ]} />

      <SerifHeadline>{dir.label}</SerifHeadline>
      <SerifSummary>
        {totalAligned >= 4
          ? `${totalAligned} of 7 forces aligned ${snapshot.net_bias}.`
          : 'Forces are mixed today — no clean directional read.'}
      </SerifSummary>

      {warnings.length > 0 && (
        <div className="ws-ftrack-warnings">
          {warnings.map((f) => (
            <span key={f} className="ws-ftrack-warning-chip">{prettyFlag(f)}</span>
          ))}
        </div>
      )}

      <div className="ws-ftrack-bias-pills">
        <PillTable>
          <PillRow keyText="Bullish forces" valueText={String(bull)} tone="sage" />
          <PillRow keyText="Bearish forces" valueText={String(bear)} tone="terra" />
          <PillRow keyText="Neutral" valueText={String(neutral)} tone="steel" />
        </PillTable>
      </div>

      <SectionStrip label="7 forces" count={7}>
        <div className="ws-ftrack-list">
          {FORCE_ORDER.map(({ key, label, subtitle }) => {
            const force = snapshot[key] || {};
            return (
              <ForceRow
                key={key}
                label={label}
                subtitle={subtitle}
                force={force}
                expanded={expandedKey === key}
                onToggle={() => setExpandedKey(expandedKey === key ? null : key)}
              />
            );
          })}
        </div>
      </SectionStrip>

      <PractitionerLens label="practitioner lens">
        The forces tell you the regime. Size to the regime, not the noise.
        When 4+ align, the read is real. When they&rsquo;re mixed, sit on your hands.
      </PractitionerLens>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// FORCE ROW — direction arrow + label + magnitude + notes
// Click to expand → reveals all key/value detail as inline rows
// ═══════════════════════════════════════════════════════════
function ForceRow({ label, subtitle, force, expanded, onToggle }) {
  const dir = DIRECTION[force?.direction] || DIRECTION.neutral;
  const magTone = force?.magnitude ? (MAGNITUDE_TONES[force.magnitude] || 'steel') : null;

  return (
    <div
      className={`ws-ftrack-row ws-ftrack-row-${dir.tone} ${expanded ? 'is-expanded' : ''}`}
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
    >
      <div className="ws-ftrack-row-arrow" aria-hidden>{dir.arrow}</div>
      <div className="ws-ftrack-row-main">
        <div className="ws-ftrack-row-head">
          <span className="ws-ftrack-row-label">{label}</span>
          {force?.magnitude && (
            <span className={`ws-ftrack-row-mag ws-ftrack-row-mag-${magTone}`}>
              {force.magnitude}
            </span>
          )}
        </div>
        <div className="ws-ftrack-row-subtitle">{subtitle}</div>
        {force?.notes && (
          <div className="ws-ftrack-row-notes">{force.notes}</div>
        )}
        {expanded && <ForceDetail force={force} />}
      </div>
      <div className="ws-ftrack-row-chev" aria-hidden>{expanded ? '\u25BE' : '\u25B8'}</div>
    </div>
  );
}

function ForceDetail({ force }) {
  const SKIP = new Set(['direction', 'magnitude', 'notes', 'warnings']);
  const items = [];
  for (const k of Object.keys(force || {})) {
    if (SKIP.has(k)) continue;
    const v = force[k];
    if (v == null) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    items.push({ key: k, value: v });
  }
  if (items.length === 0) return null;
  return (
    <div className="ws-ftrack-row-detail">
      {items.map(({ key, value }) => (
        <div key={key} className="ws-ftrack-detail-row">
          <span className="ws-ftrack-detail-key">{prettyKey(key)}</span>
          <span className="ws-ftrack-detail-value">{formatValue(value)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Helpers ───
function prettyWindow(w) {
  if (!w) return '';
  return w.replace(/_/g, ' ');
}
function prettyFlag(f) {
  return String(f || '').replace(/_/g, ' ');
}
function prettyKey(k) {
  return String(k || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
function formatValue(v) {
  if (Array.isArray(v)) {
    return v.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ');
  }
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'number') return Number.isInteger(v) ? v : v.toFixed(2);
  return String(v);
}
